import { useEffect, useState } from 'react';
import { ensureSubscribed, ensureSubscribedPublic, publicPushState, pushState, sendTestPush, type PushState as PushS } from '../push';
import { useAuth } from '../auth';
import { toast } from '../toast';
import { haptic } from '../haptic';

export default function PushButton() {
  const { user } = useAuth();
  const [state, setState] = useState<PushS>('default');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => { (user ? pushState() : publicPushState()).then(setState); }, [user]);

  if (state === 'unsupported') {
    return <Card title="Push notifications" body="This browser doesn't support web push." action={null} />;
  }
  if (state === 'denied') {
    return <Card title="Push notifications" body="Blocked. Enable in your browser/system settings, then refresh." action={null} />;
  }
  if (!user) {
    return (
      <Card
        title="Public announcements"
        body={state === 'granted'
          ? 'Subscribed to anonymous announcement pushes on this device.'
          : 'Get league-wide announcements pushed to your lock screen, no account needed.'}
        action={
          <button
            onClick={async () => {
              setBusy(true); setMsg(null);
              try {
                const r = await ensureSubscribedPublic();
                if (r.ok) { toast.success('Subscribed to announcements.'); haptic.success(); }
                else { toast.error(r.reason ?? 'Failed.'); haptic.error(); }
                setMsg(r.ok ? 'Subscribed.' : (r.reason ?? 'Failed.'));
                const s = await publicPushState();
                setState(s);
              } finally { setBusy(false); }
            }}
            disabled={busy}
            className="h-8 px-3 rounded-lg bg-pitch-500 text-ink-950 text-xs font-semibold disabled:opacity-50"
          >{state === 'granted' ? 'Re-sync' : 'Subscribe'}</button>
        }
        msg={msg}
      />
    );
  }

  async function enable() {
    setBusy(true);
    setMsg(null);
    try {
      const r = await ensureSubscribed();
      if (r.ok) { toast.success('Push notifications enabled.'); haptic.success(); }
      else { toast.error(r.reason ?? 'Failed.'); haptic.error(); }
      setMsg(r.ok ? 'Subscribed. You will get push notifications on this device.' : (r.reason ?? 'Failed.'));
      const s = await pushState();
      setState(s);
    } finally {
      setBusy(false);
    }
  }
  async function test() {
    setBusy(true);
    setMsg(null);
    try {
      const r = await sendTestPush();
      const sent = r.delivered > 0;
      setMsg(sent ? 'Test push sent.' : 'No active subscription on this device.');
      if (sent) { toast.success('Test push sent.'); haptic.tap(); }
      else { toast.error('No active subscription on this device.'); haptic.error(); }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title="Push notifications"
      body={state === 'granted'
        ? 'Enabled on this device. Notifications will arrive on lock screen.'
        : 'Get bell notifications pushed to your lock screen even when the app is closed.'}
      action={
        <div className="flex gap-2">
          <button
            onClick={enable}
            disabled={busy}
            className="h-8 px-3 rounded-lg bg-pitch-500 text-ink-950 text-xs font-semibold hover:bg-pitch-400 disabled:opacity-50"
          >
            {state === 'granted' ? 'Re-sync' : 'Enable'}
          </button>
          {state === 'granted' && (
            <button
              onClick={test}
              disabled={busy}
              className="h-8 px-3 rounded-lg bg-ink-800 text-chalk text-xs font-medium hover:bg-ink-700 disabled:opacity-50"
            >Send test</button>
          )}
        </div>
      }
      msg={msg}
    />
  );
}

function Card({ title, body, action, msg }: { title: string; body: string; action: React.ReactNode; msg?: string | null }) {
  return (
    <section className="card p-3 flex items-center justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="text-xs font-semibold">{title}</div>
        <div className="text-[11px] text-dim mt-0.5">{body}</div>
        {msg && <div className="text-[10px] text-pitch-400 mt-1">{msg}</div>}
      </div>
      {action}
    </section>
  );
}
